// context/InvoiceProvider.jsx
import { useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAlert } from '../hooks/useAlert';
import { InvoiceContext } from './invoiceContext';

const API_URL = process.env.REACT_APP_API_URL;

export function InvoiceProvider({ children }) {
  const [invoices, setInvoices] = useState([]);
  const [invoicesHasMore, setInvoicesHasMore] = useState(true);
  const [invoicesTotal, setInvoicesTotal] = useState(0);
  const cursorRef = useRef(null);
  const hasMoreRef = useRef(true);

  const [customerInvoices, setCustomerInvoices] = useState([]);
  const [customerHasMore, setCustomerHasMore] = useState(true);
  const customerCursorRef = useRef(null);
  const customerHasMoreRef = useRef(true);

  const [invoiceData, setInvoiceData] = useState([]);
  const [customerData, setCustomerData] = useState([]);
  const [invoiceItems, setInvoiceItems] = useState([]);

  const { showAlert } = useAlert();
  const navigate = useNavigate();

  const fetchInvoices = useCallback(async (reset = false, search = '', sort = 'date_desc') => {
    try {
      const currentCursor = reset ? null : cursorRef.current;
      if (!reset && !hasMoreRef.current) return;

      const res = await axios({
        method: 'GET',
        url: `${API_URL}/invoices`,
        params: { cursor: currentCursor, limit: 10, search: search || undefined, sort },
        withCredentials: true
      });

      const { invoices: newInvoices, pagination } = res.data;
      setInvoices(prev => (reset ? newInvoices : [...prev, ...newInvoices]));
      cursorRef.current = pagination.nextCursor;
      hasMoreRef.current = pagination.hasMore;
      setInvoicesHasMore(pagination.hasMore);
      setInvoicesTotal(pagination.totalCount);
    } catch (err) {
      showAlert('Fehler beim Laden der Rechnungen', 'error');
    }
  }, [showAlert]);

  const fetchCustomerInvoices = useCallback(async (customerId, reset = false) => {
    try {
      const currentCursor = reset ? null : customerCursorRef.current;
      if (!reset && !customerHasMoreRef.current) return;

      const res = await axios.get(`${API_URL}/invoices/${customerId}`, {
        params: { cursor: currentCursor, limit: 10 },
        withCredentials: true
      });

      const { invoices: newInvoices, pagination } = res.data;
      setCustomerInvoices(prev => (reset ? newInvoices : [...prev, ...newInvoices]));
      customerCursorRef.current = pagination.nextCursor;
      customerHasMoreRef.current = pagination.hasMore;
      setCustomerHasMore(pagination.hasMore);
    } catch (error) {
      showAlert('Fehler beim Laden der Rechnung.', 'error');
    }
  }, [showAlert]);

  const fetchOneInvoice = useCallback(async (id) => {
    try {
      const res = await axios.get(`${API_URL}/invoices/invoice/${id}`, { withCredentials: true });
      setInvoiceData(res.data.invoice);
      setCustomerData(res.data.invoice.customer);
      setInvoiceItems(res.data.invoice.items);
    } catch (error) {
      showAlert('Fehler beim Laden der Rechnungsdaten', 'error');
    }
  }, [showAlert]);

  const handleDelete = useCallback(async (id) => {
    try {
      const confirmed = window.confirm('Möchten Sie die Rechnung wirklich löschen?');
      if (!confirmed) return;

      await axios.delete(`${API_URL}/invoices/invoice/${id}`, { withCredentials: true });
      navigate('/dashboard/invoice');
      showAlert('Rechnung erfolgreich gelöscht', 'success');
    } catch (error) {
      showAlert('Fehler beim Löschen der Rechnung', 'error');
    }
  }, [navigate, showAlert]);

  const handleSetPaid = useCallback(async (paidValue, id) => {
    try {
      await axios.patch(
        `${API_URL}/invoices/invoice/${id}`,
        { isPaid: paidValue },
        { withCredentials: true }
      );
      setInvoiceData(prev => ({ ...prev, isPaid: paidValue }));
    } catch (error) {
      showAlert('Fehler beim Aktualisieren des Zahlungsstatus', 'error');
    }
  }, [showAlert]);

  const handleCreateInvoice = useCallback(async (
    selectedCustomer,
    actionDate,
    actionDateTo,
    invoiceDate,
    user,
    items,
    paymentMethod,
    tax,
    isPaid
  ) => {
    if (!selectedCustomer.name) {
      showAlert('Du musst noch einen Kunden auswählen', 'error');
      return;
    }

    try {
      const formattedActionDate = `${actionDate.getFullYear()}-${String(actionDate.getMonth() + 1).padStart(2, '0')}-${String(actionDate.getDate()).padStart(2, '0')}`;
      const formattedActionDateTo = actionDateTo ? actionDateTo.toISOString().split('T')[0] : undefined;

      const res = await axios({
        method: 'POST',
        url: `${API_URL}/invoices`,
        withCredentials: true,
        data: {
          user,
          customer: selectedCustomer?._id,
          items,
          actionDate: formattedActionDate,
          actionDateTo: formattedActionDateTo,
          invoiceData: invoiceDate.toISOString().split('T')[0],
          paymentMethod,
          tax,
          isPaid
        }
      });
      showAlert('Rechnung erfolgreich erstellt.', 'success');
      const invoiceID = res.data.data.invoice?._id;
      setTimeout(() => navigate(`/dashboard/invoice/${invoiceID}`), 1000);
    } catch (error) {
      showAlert('Fehler beim Erstellen der Rechnung', 'error');
    }
  }, [navigate, showAlert]);

  const saveEditedInvoice = useCallback(async (
    id,
    items,
    totalNet,
    totalBrut,
    actionDate,
    actionDateTo,
    invoiceDate,
    customerId,
    paymentMethod
  ) => {
    try {
      await axios.patch(
        `${API_URL}/invoices/invoice/${id}`,
        {
          items,
          totalNet,
          totalBrut,
          actionDate,
          actionDateTo,
          invoiceDate,
          customer: customerId,
          paymentMethod
        },
        { withCredentials: true }
      );
      showAlert('Rechnung erfolgreich gespeichert', 'success');
      setTimeout(() => window.location.reload(), 1000);
    } catch (error) {
      showAlert('Fehler beim Speichern!', 'error');
    }
  }, [showAlert]);

  const value = {
    invoices,
    fetchInvoices,
    invoicesHasMore,
    invoicesTotal,
    customerInvoices,
    fetchCustomerInvoices,
    customerHasMore,
    fetchOneInvoice,
    invoiceData,
    customerData,
    invoiceItems,
    handleDelete,
    handleSetPaid,
    handleCreateInvoice,
    saveEditedInvoice
  };

  return (
    <InvoiceContext.Provider value={value}>
      {children}
    </InvoiceContext.Provider>
  );
}